import { format } from 'date-fns';
import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StockModel, MeasureUnitModel, SupplierModel, TallyLedgerOption, INDIAN_STATES, Modules, Responsibility } from 'shared/models';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { Observable, Observer, subscribeOn, Subscription } from 'rxjs';
import { AlertMessageService } from 'shared/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { LatestRequestToken } from './latest-request-token';
import { DEFAULT_TALLY_LEDGER_SIDE, TallyLedgerSide, sideForLinkedLedger } from './tally-ledger-side';
import { TallySyncRunStatusComponent } from 'src/PmsUIApp/Features/TallyIntegration/components/tally-sync-run-status/TallySyncRunStatus.component';
import {
  GST_REGISTRATION_TYPES, gstRegistrationTypeLabel, gstRegistrationTypeRequiresGstin,
  gstRegistrationTypeSkipsGstinFormat, isKnownGstRegistrationType, normalizeGstRegistrationType
} from './gst-registration-types';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { AuthService } from 'shared/core';
import { CommonService } from 'shared/core';

@Component({
    selector: 'app-supplier-list',
    templateUrl: './supplier-list.component.html',
    styleUrls: ['./supplier-list.component.css'],
    standalone: false
})
export class SupplierListComponent implements OnInit {
  validateForm!: UntypedFormGroup;
  ApiUrl = environment.Api_Url;
  SupplierList: SupplierModel[] = [];
  SupplierListOriginal: SupplierModel[] = [];
  NewSupplier: SupplierModel = new SupplierModel;
  isVisible = false;
  isLoading: boolean = false;
  isTableLoading: boolean = false;
  typeList: any[] = [];
  PopUpTitle: string = "Add New Supplier";
  NameError = 'Enter Supplier name';
  exportoptions = {
    headers: ["Supplier Code", "Supplier Name", "Email", "Supplier Contact Number", "Contact Person Name", "Contact Person Number", "Address", "City", "State", "Country", "Pincode", "GST", "GST Type", "Tally Ledger"]
  };
  fields: any;
  exportfields: any[] = [];
  permission = {
    View: false,
    Add: false,
    Delete: false
  }
  states = INDIAN_STATES;
  /** Tally's own spellings, so a picked ledger's type always has an option here. */
  gstRegistrationTypes = GST_REGISTRATION_TYPES;
  gstRegistrationTypeLabel = gstRegistrationTypeLabel;

  // Client-side pagination state, needed to compute each row's serial number.
  pageIndex = 1;
  pageSize = 20;

  // Location filters (null = all), with options derived from the loaded list
  // so they only ever offer values that exist.
  cityFilter: string | null = null;
  stateFilter: string | null = null;
  countryFilter: string | null = null;
  /** Disabled suppliers are hidden by default; the filter surfaces them for re-enabling. */
  statusFilter: 'active' | 'disabled' | 'all' = 'active';
  cityOptions: string[] = [];
  stateOptions: string[] = [];
  countryOptions: string[] = [];

  // ---------------------------------------------------------------- //
  //  Tally ledger mapping                                             //
  // ---------------------------------------------------------------- //

  /** Whole section is hidden unless this company uses the Tally integration. */
  isTallyEnabled = false;
  /** Accounts - Tally Integration View — who may see and manage ledger mappings. */
  hasTallyPermission = false;
  tallyLedgers: TallyLedgerOption[] = [];
  /** Which Sundry group the picker lists ledgers from. */
  tallyLedgerSide: TallyLedgerSide = DEFAULT_TALLY_LEDGER_SIDE;
  isTallyLedgersLoading = false;
  isRequestingLedgerSync = false;
  /** The progress line under the ledger refresh button. */
  @ViewChild('ledgerSyncStatus') ledgerSyncStatus?: TallySyncRunStatusComponent;
  @ViewChild('tallyLedgerSelect') tallyLedgerSelect?: NzSelectComponent;
  /** Only the latest ledger-list request may apply its response (side switches and searches overlap). */
  private readonly tallyLedgersRequest = new LatestRequestToken<TallyLedgerOption[]>();
  /** The search text of the latest ledger-list request, so a reload on another side keeps the filter the user typed. */
  private tallyLedgerSearch = '';

  /**
   * Form controls currently carrying a value copied from the Tally ledger. They
   * are disabled while linked: two systems holding two versions of one GSTIN is
   * exactly what makes a pushed voucher fail its own party's GST return.
   */
  tallyLockedFields: string[] = [];

  /**
   * Differences found when a supplier that is already linked is re-opened —
   * shown rather than silently overwritten, because a GSTIN changing under an
   * accountant is the kind of edit that only surfaces at filing time.
   */
  tallyMismatches: { label: string; erp: string; tally: string }[] = [];

  /**
   * Details Tally holds in a form the ERP will not accept, so they are filled in
   * but deliberately left unlocked for the user to put right. Tally does not
   * validate a pincode, and a five-digit one there would otherwise arrive here
   * locked - unfixable, and skipped by the form's own validator, because Angular
   * excludes disabled controls when it decides whether a form is valid.
   */
  tallyUnlockedFields: { label: string; tally: string }[] = [];

  /** Which supplier field each Tally ledger detail fills. */
  private static readonly TALLY_PARTY_FIELDS: {
    control: string;
    label: string;
    pick: (option: TallyLedgerOption) => string | null | undefined;
    /** When given and the Tally value fails it, the field stays editable. */
    accepts?: (value: string) => boolean;
    /**
     * False when a rejected Tally value must not be copied in at all: a select
     * cannot show a value it has no option for, so it would save unseen.
     */
    fillsRejected?: boolean;
  }[] = [
    { control: 'Gst', label: 'GSTIN', pick: o => o.Gstin },
    {
      control: 'GstRegistrationType', label: 'GST registration type',
      pick: o => normalizeGstRegistrationType(o.GstRegistrationType),
      accepts: value => isKnownGstRegistrationType(value),
      fillsRejected: false
    },
    { control: 'Address', label: 'Address', pick: o => o.Address },
    { control: 'State', label: 'State', pick: o => o.StateName },
    { control: 'Country', label: 'Country', pick: o => o.CountryName },
    {
      control: 'Pincode', label: 'Pincode', pick: o => o.Pincode,
      accepts: value => /^[1-9][0-9]{5}$/.test(value)
    }
  ];

  /**
   * The ledger column and picker need both the tenant flag and the user
   * permission — the server withholds the field otherwise, so showing the
   * column would only render blanks.
   */
  get showTallyMapping(): boolean {
    return this.isTallyEnabled && this.hasTallyPermission;
  }

  /**
   * On a Tally-enabled company, supplier changes also shape Tally ledger
   * mappings, so Add/Edit additionally needs the Tally permission. On a
   * Tally-disabled tenant the Supplier permission alone decides, as before.
   * The server enforces the same rule.
   */
  get canModifySupplier(): boolean {
    return !this.isTallyEnabled || this.hasTallyPermission;
  }

  constructor(private fb: UntypedFormBuilder, public http: HttpClient, private alertService: AlertMessageService, private modalService: NzModalService, private auth: AuthService, private router: Router, private commonService: CommonService) { }

  ngOnInit() {
    this.permission.View = this.auth.CheckResponsibility(Modules.Supplier, Responsibility.View);
    this.permission.Add = this.auth.CheckResponsibility(Modules.Supplier, Responsibility.Add);
    this.permission.Delete = this.auth.CheckResponsibility(Modules.Supplier, Responsibility.Delete);
    this.hasTallyPermission = this.auth.CheckResponsibility(Modules.AccountsTallyIntegration, Responsibility.View);
    if (this.permission.View != true) {
      this.router.navigate(['/home/unauthorized']);
    }
    this.validateForm = this.fb.group({
      SupplierName: [null, [Validators.required, Validators.minLength(3)], [this.NameAsyncValidator]],
      ContactPersonName: [null, [Validators.required]],
      Email: [null, [Validators.email]],
      //SupplierContactNumber: [null, [Validators.required, Validators.pattern("^((\\+91-?)|0)?[0-9]{10}$")]],
      SupplierContactNumber: [null, [Validators.required]],
      ContactPersonNumber: [null, [Validators.required]],
      Address: [null, [Validators.required]],
      Gst: [null, [this.gstinValidator]],
      City: [null],
      State: [null],
      Country: ['India'],
      Pincode: [null, [this.pincodeValidator]],
      GstRegistrationType: [null]
    });
    // GSTIN validity depends on the registration type (Unknown skips the
    // format) and the state (prefix cross-check), so re-validate on both.
    this.validateForm.get('GstRegistrationType')?.valueChanges.subscribe(() =>
      this.validateForm.get('Gst')?.updateValueAndValidity());
    this.validateForm.get('State')?.valueChanges.subscribe(() =>
      this.validateForm.get('Gst')?.updateValueAndValidity());
    this.validateForm.get('Country')?.valueChanges.subscribe(() =>
      this.validateForm.get('Pincode')?.updateValueAndValidity());
    this.loadTallyFlag();
    this.GetAllSupplier();

    // Toggle can only be ON when Email holds a syntactically valid address.
    // If the email becomes empty/invalid after the user enabled notifications,
    // flip the toggle OFF so we never persist an opt-in we can't deliver to.
    this.validateForm.get('Email')?.valueChanges.subscribe(() => {
      if (!this.isEmailValidForNotification && this.NewSupplier.IsEmailNotificationEnabled) {
        this.NewSupplier.IsEmailNotificationEnabled = false;
      }
    });
  }
  get f() { return this.validateForm.controls; }

  isAboutDrawerOpen = false;

  openAboutDrawer(): void {
    this.isAboutDrawerOpen = true;
  }

  closeAboutDrawer(): void {
    this.isAboutDrawerOpen = false;
  }

  /** Regular / Composition / Regular - SEZ suppliers must carry a GSTIN. */
  get isGstinMandatory(): boolean {
    return gstRegistrationTypeRequiresGstin(this.validateForm?.get('GstRegistrationType')?.value);
  }

  /**
   * GSTIN requiredness follows the registration type (Regular/Composition/Regular - SEZ
   * must have one); the format + state-prefix checks are gated on the GST or
   * type field being `dirty` to mirror the server: they fire only when someone
   * is actually changing those values, so a legacy supplier carrying an old
   * free-text GST entry can still be edited untouched. Unknown (foreign)
   * suppliers skip the format entirely — they have no Indian GSTIN.
   */
  gstinValidator = (control: UntypedFormControl): ValidationErrors | null => {
    const value = `${control.value ?? ''}`.trim().toUpperCase();
    if (!value) {
      return this.isGstinMandatory ? { gstinRequired: true } : null;
    }
    if (gstRegistrationTypeSkipsGstinFormat(this.validateForm?.get('GstRegistrationType')?.value)) return null;
    const typeDirty = this.validateForm?.get('GstRegistrationType')?.dirty ?? false;
    if (!control.dirty && !(typeDirty && this.isGstinMandatory)) return null;
    if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value)) {
      return { gstinFormat: true };
    }
    const stateName = this.validateForm?.get('State')?.value;
    const state = this.states.find(s => s.name === stateName);
    if (state && !value.startsWith(state.gstCode)) {
      return { gstinState: true };
    }
    return null;
  };

  get gstErrorTip(): string {
    const gst = this.validateForm?.get('Gst');
    if (gst?.hasError('gstinRequired')) {
      return `GSTIN is required for a ${this.validateForm?.get('GstRegistrationType')?.value} supplier`;
    }
    if (gst?.hasError('gstinState')) {
      const stateName = this.validateForm?.get('State')?.value;
      const state = this.states.find(s => s.name === stateName);
      return `GSTIN must start with ${state?.gstCode} for ${state?.name}`;
    }
    return 'Enter a valid 15-character GSTIN, e.g. 09ABCDE1234F1Z5';
  }

  /** Indian PIN codes only — a foreign supplier's postal code is free-form. */
  pincodeValidator = (control: UntypedFormControl): ValidationErrors | null => {
    const value = `${control.value ?? ''}`.trim();
    if (!value) return null;
    const country = `${this.validateForm?.get('Country')?.value ?? ''}`.trim();
    if (country && country.toLowerCase() !== 'india') return null;
    return /^[1-9][0-9]{5}$/.test(value) ? null : { pincode: true };
  };

  get isIndia(): boolean {
    const country = `${this.validateForm?.get('Country')?.value ?? ''}`.trim();
    return !country || country.toLowerCase() === 'india';
  }

  loadTallyFlag(): void {
    this.commonService.getConfigValue('TallyIntegrationEnabled').subscribe(val => {
      this.isTallyEnabled = String(val).toLowerCase() === 'true';
      if (this.showTallyMapping) {
        this.loadTallyLedgers();
      }
    });
  }

  /**
   * Only ledgers no other supplier has claimed — the mapping is one-to-one, so
   * a ledger already linked elsewhere must not be offered again.
   */
  loadTallyLedgers(
    search: string = '', afterLoad?: () => void, keepPendingAfterLoad = false, onOwnResponse?: (ledgers: TallyLedgerOption[]) => void
  ): void {
    this.isTallyLedgersLoading = true;
    this.tallyLedgerSearch = search;
    // The supplier being edited is sent so its own ledger comes back with the
    // list — otherwise the one ledger whose details the form needs is the one
    // filtered out as already claimed.
    const params: string[] = [];
    if (search) params.push('search=' + encodeURIComponent(search));
    if (this.NewSupplier?.SupplierId > 0) params.push('supplierid=' + this.NewSupplier.SupplierId);
    params.push('side=' + this.tallyLedgerSide);
    params.push('owner=Supplier');
    const url = this.ApiUrl + 'tally/getunclaimedledgers' + (params.length ? '?' + params.join('&') : '');

    // A search or side switch that overlaps the form-opening load must not lose
    // its afterLoad (reconciling the linked ledger): it is carried forward and
    // runs with whichever response is the latest. onOwnResponse is different: it
    // reads the request's own response even when that has gone stale, because the
    // opening response is the unfiltered one that says which side the linked
    // ledger is on, which a later search response (filtered by name) may not.
    const token = keepPendingAfterLoad
      ? this.tallyLedgersRequest.beginKeepingPending(onOwnResponse)
      : this.tallyLedgersRequest.begin(afterLoad, onOwnResponse);
    this.http.get<TallyLedgerOption[]>(url).subscribe({
      next: (res) => {
        const ledgers = res ?? [];
        // A newer request (another side or search) is in flight: this answer is stale.
        const followUp = this.tallyLedgersRequest.completeWith(token, ledgers);
        if (!followUp) return;
        this.tallyLedgers = ledgers;
        this.isTallyLedgersLoading = false;
        followUp();
      },
      error: () => {
        const followUp = this.tallyLedgersRequest.complete(token);
        if (!followUp) return;
        // Not fatal: the field simply offers nothing, and the supplier can
        // still be saved by someone who does not need to map it yet.
        this.tallyLedgers = [];
        this.isTallyLedgersLoading = false;
        followUp();
      }
    });
  }

  /**
   * Picking a ledger is a deliberate act, so Tally's details win outright and
   * the fields they filled lock. Clearing it releases them with their values
   * intact — the supplier keeps what it had, it is simply editable again.
   */
  onTallyLedgerChange(ledgerName: string | null): void {
    this.applyTallyParty(this.findTallyLedger(ledgerName), true);
  }

  /** Adopts Tally's values for every field that differed. */
  useTallyValues(): void {
    this.applyTallyParty(this.findTallyLedger(this.NewSupplier?.TallyLedgerName), true);
  }

  private findTallyLedger(ledgerName: string | null | undefined): TallyLedgerOption | null {
    if (!ledgerName) return null;
    return this.tallyLedgers.find(l => l.LedgerName === ledgerName) ?? null;
  }

  /**
   * Copies the ledger's party details onto the form and locks what it filled.
   *
   * A field Tally has nothing for is left alone and stays editable — plenty of
   * creditor ledgers are name-only, and locking a blank GSTIN would leave nobody
   * able to enter one.
   *
   * @param overwrite true when the user just picked the ledger; false when an
   * existing mapping is re-opened, where only blanks are filled and anything
   * that disagrees is reported instead.
   */
  private applyTallyParty(option: TallyLedgerOption | null, overwrite: boolean): void {
    this.unlockTallyFields();
    this.tallyMismatches = [];
    this.tallyUnlockedFields = [];
    if (!option) return;

    for (const field of SupplierListComponent.TALLY_PARTY_FIELDS) {
      const tallyValue = `${field.pick(option) ?? ''}`.trim();
      if (!tallyValue) continue;

      const control = this.validateForm.get(field.control);
      if (!control) continue;

      const current = `${control.value ?? ''}`.trim();
      const usable = field.accepts ? field.accepts(tallyValue) : true;

      // A value Tally cannot be trusted on never overwrites a good one here.
      const fills = usable || field.fillsRejected !== false;
      if (fills && ((overwrite && usable) || !current)) {
        control.setValue(tallyValue);
      } else if (usable && current.toLowerCase() !== tallyValue.toLowerCase()) {
        this.tallyMismatches.push({ label: field.label, erp: current, tally: tallyValue });
      }

      if (!usable) {
        // Left editable on purpose: locking it would hand the user a value they
        // can see is wrong and cannot correct.
        this.tallyUnlockedFields.push({ label: field.label, tally: tallyValue });
        control.updateValueAndValidity({ emitEvent: false });
        continue;
      }

      control.disable({ emitEvent: false });
      this.tallyLockedFields.push(field.control);
    }
  }

  private unlockTallyFields(): void {
    for (const name of this.tallyLockedFields) {
      this.validateForm.get(name)?.enable({ emitEvent: false });
    }
    this.tallyLockedFields = [];
  }

  onTallyLedgerSearch(term: string): void {
    this.loadTallyLedgers(term, undefined, true);
  }

  /**
   * Switching side only changes which list is offered. The ledger already
   * picked stays selected (tallyLedgerOptions keeps showing it), so flipping the
   * switch to look around never unlinks or refills anything.
   */
  onTallyLedgerSideChange(): void {
    // Text typed for the old side would filter the new side's list.
    this.tallyLedgerSelect?.clearInput();
    this.loadTallyLedgers('', undefined, true);
  }

  /**
   * The supplier's own mapping is not in the unclaimed list (it is claimed —
   * by this supplier), so it is prepended while editing to keep the select
   * showing its current value.
   */
  get tallyLedgerOptions(): TallyLedgerOption[] {
    const current = this.NewSupplier?.TallyLedgerName;
    if (current && !this.tallyLedgers.some(l => l.LedgerName === current)) {
      return [{ LedgerName: current, ParentGroupName: '', LastSyncedDate: '' }, ...this.tallyLedgers];
    }
    return this.tallyLedgers;
  }

  /**
   * Queues a fresh read of Tally's ledger list through the agent. Tally is a
   * desktop application, so the refresh lands whenever the agent next reaches
   * it; the progress line follows it and re-reads the list when it completes.
   */
  requestLedgerSync(): void {
    this.isRequestingLedgerSync = true;
    this.http.post<any>(this.ApiUrl + 'tally/requestledgersync', {}).subscribe({
      next: (res) => {
        this.isRequestingLedgerSync = false;
        this.alertService.success(typeof res === 'string' ? res : 'A ledger refresh from Tally has been requested.');
        this.ledgerSyncStatus?.watch();
      },
      error: (err) => {
        this.isRequestingLedgerSync = false;
        this.alertService.error(typeof err?.error === 'string' ? err.error : 'The ledger refresh could not be requested. Please try again.');
        // Refused because one is already running: follow that one.
        this.ledgerSyncStatus?.watch();
      }
    });
  }

  get isEmailValidForNotification(): boolean {
    const emailCtrl = this.validateForm.get('Email');
    const value = emailCtrl?.value;
    if (!value || `${value}`.trim() === '') return false;
    return !emailCtrl?.errors;
  }
  onKeydown(event: any) {
    if (event.target.selectionStart === 0 && event.code === "Space" || event.key === "Enter" && event.keyCode === 13) {

      event.preventDefault();
      event = this.search()
    }
  }
  ValidateText() {
    this.searchValue.trim()
    this.searchValue = this.searchValue.trim();

  }
  GetAllSupplier() {
    this.isTableLoading = true;
    // The detailed feed: includes the Tally ledger mapping only when the
    // server confirms the caller's Accounts - Tally Integration permission.
    // Dropdowns elsewhere keep using supplier/getallsuppliers, which never
    // carries the mapping.
    let url = this.ApiUrl + "supplier/getsupplierlistdetailed";
    this.http.get<SupplierModel[]>(url).subscribe(res => {
      this.SupplierList = res;
      this.SupplierListOriginal = res;
      this.cityOptions = this.distinctValues(res.map(x => x.City));
      this.stateOptions = this.distinctValues(res.map(x => x.State));
      this.countryOptions = this.distinctValues(res.map(x => x.Country));
      this.applyFilters();
      this.exportfields = [];
      this.SupplierList.forEach(x => {
        this.fields = {};
        this.fields.SupplierCode = x.SupplierCode;
        this.fields.SupplierName = x.SupplierName;
        this.fields.Email = x.Email;
        this.fields.SupplierContactNumber = x.SupplierContactNumber;
        this.fields.ContactPersonName = x.ContactPersonName;
        this.fields.ContactPersonNumber = x.ContactPersonNumber;
        this.fields.Address = x.Address;
        this.fields.City = x.City;
        this.fields.State = x.State;
        this.fields.Country = x.Country;
        this.fields.Pincode = x.Pincode;
        this.fields.Gst = x.Gst;
        this.fields.GstRegistrationType = x.GstRegistrationType;
        this.fields.TallyLedgerName = x.TallyLedgerName;
        this.exportfields.push(this.fields);

      })
      this.isTableLoading = false;
    }, res => { this.GetAllSupplier(); });
  }
  SaveSupplier() {
    if (this.validateForm.invalid) {
      Object.values(this.validateForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      this.isLoading = false
      return;
    }

    this.NewSupplier.SupplierName = this.f['SupplierName'].value;
    this.NewSupplier.ContactPersonName = this.f['ContactPersonName'].value;
    this.NewSupplier.Email = this.f['Email'].value;
    this.NewSupplier.SupplierContactNumber = this.f['SupplierContactNumber'].value;
    this.NewSupplier.ContactPersonNumber = this.f['ContactPersonNumber'].value;
    this.NewSupplier.Address = this.f['Address'].value;
    this.NewSupplier.Gst = this.f['Gst'].value;
    this.NewSupplier.City = this.f['City'].value;
    this.NewSupplier.State = this.f['State'].value;
    this.NewSupplier.Country = this.f['Country'].value;
    this.NewSupplier.Pincode = this.f['Pincode'].value;
    this.NewSupplier.GstRegistrationType = this.f['GstRegistrationType'].value;
    // TallyLedgerName is bound directly via [(ngModel)] on the picker.
    // IsEmailNotificationEnabled is bound directly via [(ngModel)]
    // on the nz-switch — no form-control mapping needed. It flows back
    // to the backend on the existing supplier save payload.
    // if(!this.Validate(this.NewSupplier))
    // {
    //   this.isLoading=false
    //     return;
    // }
    let url = this.ApiUrl + "supplier/addupdatesupplier";
    this.http.post<SupplierModel>(url, this.NewSupplier).subscribe({

      next: res => {
        this.alertService.success("Supplier Saved Successfully");
        this.isLoading = this.isVisible = false;
        this.GetAllSupplier();
        if (this.isTallyEnabled) { this.loadTallyLedgers(); }
      },
      error: res => {
        // The server's refusal reasons (invalid GSTIN, ledger already
        // claimed, …) are written for the user — show them as-is. The modal
        // stays open so the correction can be made in place.
        this.alertService.error(typeof res?.error === 'string' ? res.error : "An error has been occured. Please try again");
        this.isLoading = false;
      },

    });

  }
  Validate(model: SupplierModel) {
    var Isvalid: boolean = true;
    const emailPattern = "^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$";
    if (model.SupplierName == '') {
      this.alertService.error("Enter Supplier Name"); Isvalid = false;
    }
    else if (model.ContactPersonName == '') {
      this.alertService.error("Enter Contact Person Name"); Isvalid = false;
    }
    // else if (model.Email == '') {
    //   this.alertService.error("Enter Email"); Isvalid = false;
    // }

    return Isvalid;
  }
  OpenEditPop(data: SupplierModel) {
    this.PopUpTitle = "Modify Supplier Details";
    // Locks from whichever supplier was edited last would otherwise carry over
    // and freeze fields on this one.
    this.unlockTallyFields();
    this.tallyMismatches = [];
    this.tallyUnlockedFields = [];
    this.validateForm.setValue(
      {
        SupplierName: data.SupplierName,
        ContactPersonName: data.ContactPersonName,
        Email: data.Email,
        SupplierContactNumber: data.SupplierContactNumber,
        ContactPersonNumber: data.ContactPersonNumber,
        Address: data.Address,
        Gst: data.Gst,
        City: data.City || null,
        State: data.State || null,
        Country: data.Country || 'India',
        Pincode: data.Pincode || null,
        // Saved before the list matched Tally: Overseas reads as Unknown, SEZ as Regular - SEZ.
        GstRegistrationType: normalizeGstRegistrationType(data.GstRegistrationType)
      }
    )
    // A fresh edit starts pristine: the GSTIN validator is dirty-gated so a
    // legacy free-text GST entry only gets validated once someone changes it.
    this.validateForm.markAsPristine();
    this.NewSupplier = new SupplierModel();
    this.NewSupplier.SupplierId = data.SupplierId;
    this.NewSupplier.SupplierCode = data.SupplierCode || '';
    this.NewSupplier.TallyLedgerName = data.TallyLedgerName || '';
    this.NewSupplier.IsEmailNotificationEnabled = data.IsEmailNotificationEnabled || false;

    if (this.showTallyMapping && this.NewSupplier.TallyLedgerName) {
      // Re-read so this supplier's own ledger (and its party details) is in the
      // list, then reconcile without overwriting: an existing supplier's saved
      // values are shown as they are, with any disagreement surfaced.
      this.tallyLedgerSide = DEFAULT_TALLY_LEDGER_SIDE;
      const opening = this.NewSupplier;
      this.loadTallyLedgers(
        '',
        () => {
          // Only reached when the opening response did not move the form to the
          // other side (or was lost): look at the list now showing, then reconcile.
          if (!this.openOnLinkedLedgerSide(this.tallyLedgers)) {
            this.reconcileLinkedTallyLedger();
          }
        },
        false,
        // The opening response is the unfiltered one, so it is read for the linked
        // ledger's side even if a search the user typed meanwhile has replaced the
        // list shown. Not for a form that was closed or reopened for another supplier.
        ledgers => {
          if (this.NewSupplier === opening && this.isVisible) {
            this.openOnLinkedLedgerSide(ledgers);
          }
        });
    } else if (this.showTallyMapping) {
      // No linked ledger: start on creditors with a fresh list, so neither the
      // toggle nor the options carry over from the previous time the form was open.
      this.tallyLedgerSide = DEFAULT_TALLY_LEDGER_SIDE;
      this.loadTallyLedgers('');
    }

    this.isVisible = true;
  }

  /**
   * A supplier Tally keeps under debtors is not in the creditors list, so the form
   * opens on the side its ledger is on, and reconciles after that final load. Returns
   * true when it moved the form (the reload reconciles), false when there is nothing to do.
   */
  private openOnLinkedLedgerSide(ledgers: TallyLedgerOption[]): boolean {
    const linkedSide = sideForLinkedLedger(ledgers, this.NewSupplier?.TallyLedgerName);
    if (linkedSide !== 'Debtors' || this.tallyLedgerSide === 'Debtors') return false;
    this.tallyLedgerSide = 'Debtors';
    this.loadTallyLedgers(this.tallyLedgerSearch, () => this.reconcileLinkedTallyLedger());
    return true;
  }

  private reconcileLinkedTallyLedger(): void {
    this.applyTallyParty(this.findTallyLedger(this.NewSupplier.TallyLedgerName), false);
  }

  showModal(): void {
    this.PopUpTitle = "Add New Supplier";
    this.unlockTallyFields();
    this.tallyMismatches = [];
    this.tallyUnlockedFields = [];
    this.validateForm.reset();
    //this.validateForm.setValue(
    //  {
    //    SupplierName: null,
    //    ContactPersonName: null,
    //    Email: null,
    //    SupplierContactNumber: null,
    //    ContactPersonNumber: null,
    //    Address: null,
    //    Gst: null
    //  }
    //)
    this.NewSupplier = new SupplierModel();
    // The form reset clears Country too; a fresh supplier defaults to India.
    this.validateForm.patchValue({ Country: 'India' });
    if (this.showTallyMapping) {
      // Drop any list fetched while editing a supplier: it included that
      // supplier's own ledger, which a new supplier must not be offered.
      this.tallyLedgerSide = DEFAULT_TALLY_LEDGER_SIDE;
      this.loadTallyLedgers();
    }
    this.isVisible = true;
  }

  handleOk(): void {

    this.isLoading = true;
    this.SaveSupplier();

  }

  handleCancel(): void {
    this.isVisible = false;
  }
  export() {

    var exportdate = format(new Date(), "-ddMMyyyy-hhmmss");
    if (this.exportfields.length > 0)
      new AngularCsv(this.exportfields, 'supplier-export' + exportdate, this.exportoptions);
  }
  /**
   * "Delete" is a soft disable server-side: historical POs, GRNs and invoices
   * keep resolving the supplier, but it disappears from every dropdown. The
   * Disabled status filter on this page brings it back for re-enabling.
   */
  DeleteSupplier() {
    let url = this.ApiUrl + "supplier/deletesupplier";
    this.http.post<any>(url, this.NewSupplier.SupplierId).subscribe({

      next: res => {
        this.alertService.success(typeof res === 'string' ? res : "Supplier disabled successfully");
        this.isLoading = this.isVisible = false;
        this.GetAllSupplier();
      },
      error: res => {
        this.alertService.error(typeof res?.error === 'string' ? res.error : "An error has been occured. Please try again");
        this.isLoading = this.isVisible = false;
      },

    });

  }
  handleDelete(data: SupplierModel) {
    this.NewSupplier = data;
    const modal = this.modalService.confirm({
      nzTitle: 'Confirm',
      nzContent: `Disable supplier '${data.SupplierName}'? It will no longer appear in supplier selections; existing records keep it. You can re-enable it later from the Disabled status filter.`,
      nzOkText: 'Disable',
      nzOnOk: () => this.DeleteSupplier(),
    });

    setTimeout(() => modal.destroy(), 8000);

  }

  EnableSupplier(data: SupplierModel) {
    const modal = this.modalService.confirm({
      nzTitle: 'Confirm',
      nzContent: `Enable supplier '${data.SupplierName}' again? It will reappear in supplier selections. Email notifications stay off until re-enabled on the Edit form.`,
      nzOkText: 'Enable',
      nzOnOk: () => {
        this.http.post<any>(this.ApiUrl + "supplier/enablesupplier", data.SupplierId).subscribe({
          next: res => {
            this.alertService.success(typeof res === 'string' ? res : "Supplier enabled successfully");
            this.GetAllSupplier();
          },
          error: res => {
            this.alertService.error(typeof res?.error === 'string' ? res.error : "An error has been occured. Please try again");
          }
        });
      },
    });

    setTimeout(() => modal.destroy(), 8000);
  }
  NameAsyncValidator = (control: UntypedFormControl) =>
    new Observable((observer: Observer<ValidationErrors | null>) => {
      setTimeout(() => {
        var res = this.SupplierList.filter(x => x.SupplierId != this.NewSupplier.SupplierId);
        var nre = res.filter(x => x.SupplierName.toLowerCase() == control.value.toLowerCase());
        this.NameError = '';

        if (control.value == '') {
          this.NameError = "Enter Supplier name";
          observer.next({ error: true, duplicated: true });
        }
        else if (nre.length > 0) {
          this.NameError = "Supplier name already exist";
          // you have to return `{error: true}` to mark it as an error event
          observer.next({ error: true, duplicated: true });
        } else {
          this.NameError = "Enter Supplier name";
          observer.next(null);
        }
        observer.complete();
      }, 1);
    });
  searchValue = '';
  visible = false;
  // Frontend-only filter for the Email Notifications opt-in flag.
  notificationFilter: 'all' | 'enabled' | 'disabled' = 'all';
  // Whether the supplier is linked to a Tally ledger. Only offered when the
  // Tally column itself is visible — see matchesTallyLink for why.
  tallyLinkFilter: 'all' | 'linked' | 'unlinked' = 'all';

  reset(): void {
    this.searchValue = '';
    this.notificationFilter = 'all';
    this.cityFilter = null;
    this.stateFilter = null;
    this.countryFilter = null;
    this.statusFilter = 'active';
    this.tallyLinkFilter = 'all';
    this.applyFilters();
  }

  search(): void {
    this.visible = false;
    this.applyFilters();
  }

  // Re-runs the active text search whenever any dropdown filter changes so
  // all filters always compose against the original list.
  onFilterChange(): void {
    this.applyFilters();
  }

  // Distinct non-empty values, case-insensitively de-duplicated (first
  // spelling wins) and sorted — the feed for the location filter dropdowns.
  private distinctValues(values: (string | null | undefined)[]): string[] {
    const seen = new Map<string, string>();
    for (const value of values) {
      const trimmed = (value ?? '').trim();
      if (trimmed && !seen.has(trimmed.toLowerCase())) {
        seen.set(trimmed.toLowerCase(), trimmed);
      }
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }

  // Single source of truth for the visible rows: applies the free-text
  // search, the notification filter and the location filters together
  // against the original list.
  private applyFilters(): void {
    const term = (this.searchValue ?? '').trim().toLowerCase();
    this.SupplierList = this.SupplierListOriginal.filter(
      (item) => this.matchesText(item, term)
        && this.matchesNotification(item)
        && this.matchesStatus(item)
        && this.matchesTallyLink(item)
        && this.matchesField(item.City, this.cityFilter)
        && this.matchesField(item.State, this.stateFilter)
        && this.matchesField(item.Country, this.countryFilter)
    );
    // A changed filter can leave the previous page number past the end of the
    // shorter result — always show its first page.
    this.pageIndex = 1;
  }

  private matchesField(value: string | null | undefined, filter: string | null): boolean {
    if (!filter) return true;
    return (value ?? '').trim().toLowerCase() === filter.toLowerCase();
  }

  /** "City, State" with whichever parts exist — the Location cell's first line. */
  cityStateOf(item: SupplierModel): string {
    return [item.City, item.State]
      .map(part => (part ?? '').trim())
      .filter(part => part.length > 0)
      .join(', ');
  }

  // Null-safe text match. A null/empty field contributes NO match instead of
  // accidentally matching every term (the previous `indexOf() !== -1` on an
  // optional-chained value returned `undefined !== -1` === true for nulls).
  private matchesText(item: SupplierModel, term: string): boolean {
    if (!term) {
      return true;
    }
    const fields = [
      item.SupplierName,
      item.SupplierCode,
      item.Email,
      item.Address,
      item.City,
      item.State,
      item.ContactPersonName,
      item.ContactPersonNumber,
      item.SupplierContactNumber,
      item.Gst,
      item.TallyLedgerName,
    ];
    return fields.some((field) => (field ?? '').toLowerCase().includes(term));
  }

  private matchesStatus(item: SupplierModel): boolean {
    switch (this.statusFilter) {
      case 'active': return !item.Disabled;
      case 'disabled': return item.Disabled === true;
      default: return true;
    }
  }

  // Only meaningful when the Tally column is visible: for callers without the
  // Tally permission the server withholds TallyLedgerName entirely, so every
  // supplier would look unlinked and the filter would lie. The control is
  // hidden in that case; this guard keeps a stale selection harmless too.
  private matchesTallyLink(item: SupplierModel): boolean {
    if (!this.showTallyMapping) return true;
    const isLinked = (item.TallyLedgerName ?? '').trim().length > 0;
    switch (this.tallyLinkFilter) {
      case 'linked': return isLinked;
      case 'unlinked': return !isLinked;
      default: return true;
    }
  }

  private matchesNotification(item: SupplierModel): boolean {
    switch (this.notificationFilter) {
      case 'enabled':
        return item.IsEmailNotificationEnabled === true;
      case 'disabled':
        return !item.IsEmailNotificationEnabled;
      default:
        return true;
    }
  }

}
